import dayjs from "dayjs"
import { useCallback, useEffect, useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import type {
  ListPricingPackagesSuccess,
  PricingPackage,
  PricingPackageQueryableFields,
  Query,
} from "@/graphql/codegen/graphql"
import { PricingPackageSortField, PricingPackagesListDocument, SortingOrder } from "@/graphql/codegen/graphql"
import type { PricingPackageInfo } from "@/screens/Procurement/PurchaseOrder/ProductAdded/types/pricing"
import type { ClassifiedPricingPackages } from "@/screens/Procurement/PurchaseOrder/types"

const LIMIT = 20

const DEFAULT_SORT_SPECS = {
  field: PricingPackageSortField.EffectiveDate,
  order: SortingOrder.Asc,
} as const

export const useGetPricingPackage = ({
  filterSpecs,
  expanded,
}: {
  filterSpecs: PricingPackageQueryableFields
  expanded?: boolean // undefined means always run queries (for purchase order flow)
}): {
  pricingListError: CombinedError | undefined
  pricingList: ClassifiedPricingPackages
  pricingListFetching: boolean
  loadMore: () => void
  reexecuteQueryWithReset: () => void
} => {
  const [offset, setOffset] = useState(0)
  const [pricingData, setPricingData] = useState<PricingPackageInfo<PricingPackage>>({
    currentPricing: undefined,
    futurePricing: [],
  })
  const { productId, companyId, sourceCompanyLocationAssociationId } = filterSpecs ?? {}

  const shouldPauseQueries = expanded === false || !productId || !companyId || !sourceCompanyLocationAssociationId

  const [
    { data: currentPricingList, fetching: currentPricingListFetching, error: currentPricingListError },
    reexecuteCurrentPricingListQuery,
  ] = useQuery<Pick<Query, "pricingPackage">>({
    query: PricingPackagesListDocument,
    variables: {
      input: {
        filter: {
          ...filterSpecs,
          effectiveNow: true,
        },
        sort: DEFAULT_SORT_SPECS,
        limit: LIMIT,
        offset,
      },
    },
    pause: shouldPauseQueries,
  })

  const [
    { data: pricingListDetails, fetching: pricingListFetching, error: pricingListError },
    reexecuteSelectedPricingListQuery,
  ] = useQuery<Pick<Query, "pricingPackage">>({
    query: PricingPackagesListDocument,
    variables: {
      input: {
        filter: {
          ...filterSpecs,
          effectiveNow: null,
          effectiveDateRange: {
            startDate: dayjs().add(1, "day").format("MM/DD/YYYY"),
          },
        },
        sort: DEFAULT_SORT_SPECS,
        limit: LIMIT,
        offset,
      },
    },
    pause: shouldPauseQueries,
  })

  const reexecuteQueryWithReset = useCallback(() => {
    if (expanded === false) return
    reexecuteCurrentPricingListQuery({ requestPolicy: "network-only" })
    reexecuteSelectedPricingListQuery({ requestPolicy: "network-only" })
  }, [expanded, reexecuteCurrentPricingListQuery, reexecuteSelectedPricingListQuery])

  useEffect(() => {
    if (pricingListDetails && currentPricingList) {
      const currentPricingPackagesList =
        (currentPricingList?.pricingPackage?.list as ListPricingPackagesSuccess).pricingPackages || []
      const futurePricingPackageList =
        (pricingListDetails?.pricingPackage?.list as ListPricingPackagesSuccess).pricingPackages || []

      const now = dayjs().startOf("day")
      // If there are multiple current pricing packages, prioritize ones with valid expiration dates
      let selectedCurrentPricing = currentPricingPackagesList[0]
      if (currentPricingPackagesList.length > 1) {
        // First check for packages with expiration dates that aren't expired
        const packagesWithValidExpiration = currentPricingPackagesList.filter(
          (pkg) => pkg.expirationDate === null || dayjs(pkg.expirationDate).startOf("day").isAfter(now)
        )

        if (packagesWithValidExpiration.length > 0) {
          // If we have packages with expiration dates, take the most recent one
          selectedCurrentPricing = packagesWithValidExpiration.reduce(
            (latest, pkg) => (dayjs(pkg.effectiveDate).isAfter(dayjs(latest.effectiveDate)) ? pkg : latest),
            currentPricingPackagesList[0]
          )
        }
      }

      setPricingData((prev) => ({
        ...prev,
        currentPricing: selectedCurrentPricing,
        futurePricing: futurePricingPackageList,
      }))
    }
  }, [offset, pricingListDetails, currentPricingList])

  const loadMore = useCallback(() => {
    if (expanded === false) return
    setOffset((prevOffset) => prevOffset + LIMIT)
  }, [expanded])

  return {
    pricingList: pricingData,
    pricingListFetching: currentPricingListFetching || pricingListFetching,
    pricingListError: currentPricingListError || pricingListError,
    loadMore,
    reexecuteQueryWithReset,
  }
}
